import { Injectable } from '@angular/core'
import { Case, CaseHearing } from '@ftr/annotations-contracts'
import { AnnotationsApiClient, AnnotationsApiClientFactory } from '@ftr/api-annotations'
import { PagedResult } from '@ftr/contracts/api/core'
import { SortDirection } from '@ftr/contracts/api/shared'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { CourtSystemRegionCache } from '@ftr/ui-court-system'
import { Observable, from, map, of, shareReplay, startWith, switchMap } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  constructor(
    private readonly apiClientFactory: AnnotationsApiClientFactory,
    private readonly regionCache: CourtSystemRegionCache,
  ) {}

  getCasesPaginated(options: {
    courtSystemId: string
    page: number
    pageSize: number
    sortDirection: SortDirection
  }): ApiResult<PagedResult<Case>> {
    const { courtSystemId, page, pageSize, sortDirection } = options

    return this.getApiClient(courtSystemId).pipe(
      switchMap(client =>
        from(
          client.cases
            .getCases({
              query: {
                courtSystemId,
                limit: pageSize,
                offset: (page - 1) * pageSize,
                sortDirection,
              },
            })
            .catch(() => {
              return new Error('An unknown error occurred')
            }),
        ).pipe(
          map(result => {
            if (result instanceof Error) {
              return RemoteData.failure(result)
            }
            if (result.status !== 200) {
              return RemoteData.failure(new Error(result.body.error))
            }

            return RemoteData.success<PagedResult<Case>>({
              items: result.body.items,
              meta: { totalItems: result.body.total, number: page, size: pageSize },
            })
          }),
        ),
      ),
      startWith(RemoteData.loading()),
      shareReplay(1),
    )
  }

  getCase(caseId: string, courtSystemId: string): ApiResult<Case> {
    return this.getApiClient(courtSystemId).pipe(
      switchMap(client =>
        from(
          client.case
            .getCase({
              query: {
                courtSystemId,
              },
              params: { caseId },
            })
            .catch(() => {
              return new Error('An unknown error occurred')
            }),
        ).pipe(
          map(result => {
            if (result instanceof Error) {
              return RemoteData.failure(result)
            }
            if (result.status === 404) {
              return RemoteData.failure(new Error('Case not found'))
            }
            if (result.status !== 200) {
              return RemoteData.failure(new Error(result.body.error))
            }

            return RemoteData.success<Case>(result.body)
          }),
        ),
      ),
      startWith(RemoteData.loading()),
      shareReplay(1),
    )
  }

  getCaseHearingsPaginated(options: {
    caseId: string
    courtSystemId: string
    page: number
    pageSize: number
    sortDirection: SortDirection
  }): ApiResult<PagedResult<CaseHearing>> {
    const { caseId, courtSystemId, page, pageSize, sortDirection } = options
    return this.getApiClient(courtSystemId).pipe(
      switchMap(client =>
        from(
          client.case
            .getCaseHearings({
              query: {
                courtSystemId,
                limit: pageSize,
                offset: (page - 1) * pageSize,
                sortDirection,
              },
              params: { caseId },
            })
            .catch(() => {
              return new Error('An unknown error occurred')
            }),
        ).pipe(
          map(result => {
            if (result instanceof Error) {
              return RemoteData.failure(result)
            }

            if (result.status === 404) {
              return RemoteData.failure(new Error('Case not found'))
            }

            if (result.status !== 200) {
              return RemoteData.failure(new Error(result.body.error))
            }

            return RemoteData.success<PagedResult<CaseHearing>>({
              items: result.body.items,
              meta: { totalItems: result.body.total, number: page, size: pageSize },
            })
          }),
        ),
      ),
      startWith(RemoteData.loading()),
      shareReplay(1),
    )
  }

  private getApiClient(courtSystemId: string): Observable<AnnotationsApiClient> {
    return this.regionCache
      .getOrFetchCourtSystem(of(courtSystemId))
      .pipe(map(({ region }) => this.apiClientFactory.build(region)))
  }
}

export function getCaseTitle(caseData: Case): string {
  return caseData.caseTitle || 'Untitled Case'
}
